/**
 * Audio Subscription Component JS
 * Handles tab switching functionality and subscription buttons for the audio subscription popup
 */

class AudioSubscriptionComponent extends HTMLElement {
  constructor() {
    super();

    // Initialize component properties
    this.popup = document.getElementById('audio-subscription');

    this.closeButtons = [
      ...Array.from(this.querySelectorAll('.jsClose')),
      ...Array.from(document.querySelectorAll('.jsClose')),
    ];

    this.subscribeButton = this.querySelector('.subscribe-audio');
    this.tabButtons = Array.from(this.querySelectorAll('.tab-button'));
    this.tabContents = Array.from(this.querySelectorAll('.tab-content'));
    this.cartForm = document.querySelector('product-cart-form');
    this.itemsCart = [];
    this.apiBaseUrl = window.theme.internalApp.baseUrl;

    if (!this.popup) {
      return;
    }

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initListeners();
      });
    } else {
      this.initListeners();
    }
  }

  initListeners() {
    if (this.closeButtons && this.closeButtons.length > 0) {
      this.closeButtons.forEach(button => {
        button.addEventListener('click', this.handleClose.bind(this));
      });
    }

    if (this.subscribeButton) {
      this.subscribeButton.addEventListener('click', this.handleSubscribe.bind(this));
    }

    if (this.tabButtons) {
      this.tabButtons.forEach(button => {
        button.addEventListener('click', () => {
          const tab = button.dataset.tab;

          if (tab) {
            this.switchTab(tab);
          }
        });
      });
    }
  }

  initDownloadAppButton() {
    const downloadButton = document.querySelector('.audio-subscription-popup .get-app-button');

    if (downloadButton) {
      downloadButton.addEventListener('click', () => {
        window.open('https://apps.apple.com/us/app/leaf-from-aspen/id1532299635', '_blank');
      });
    }
  }

  switchTab(selectedTab) {
    this.tabButtons.forEach(button => button.classList.remove('active'));
    this.tabContents.forEach(content => content.classList.remove('active'));

    const selectedButton = this.tabButtons.find(button => button.dataset.tab === selectedTab),
      tabContent = this.tabContents.find(content => content.id === `${selectedTab}-content`);

    selectedButton.classList.add('active');
    tabContent.classList.add('active');
  }

  updateSelectedVariant(variantId, sellingPlan) {
    this.subscribeButton.dataset.variantId = variantId;
    this.subscribeButton.dataset.sellingPlan = sellingPlan;
  }

  async handleClose() {
    const requestCopy = this.querySelector('#request-copy');

    if (this.cartForm) {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart.status) {
        this.cartForm.notify = cart.description || cart.message;
        this.cartForm.cartRefresh({ isSuccess: false, sections: [] });
      } else {
        const sectionsToRender = this.cartForm.sections.map(section => section.section).join(',');

        const response = await fetch(`${window.location.pathname}?sections=${sectionsToRender}`);
        const sections = await response.json();

        let targetPopup = document.querySelector('.popup-noti-added');

        if (targetPopup) {
          targetPopup.style.display = 'block';

          setTimeout(() => {
            targetPopup.style.display = 'none';
          }, 5000);
        }

        this.cartForm.cartRefresh({ isSuccess: true, sections });
      }
    }

    document.body.classList.remove('show-popup');
    this.popup.classList.remove('active');

    requestCopy.dataset.currentStep = '1';
  }

  async previousCartItem(variantId) {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    this.itemsCart = cart.items;

    return cart.items.find(item => item.variant_id === parseInt(variantId, 10));
  }

  async deleteProductFromCart(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        console.error('Error updating cart:', response);
      }
    } catch (error) {
      console.error('Error deleting product from cart:', error);
      this.bannerAlert(error.message);
    }
  }

  async getProductSubscriptionFromCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    return cart.items.find(
      item => (item.properties && '_subscription_variant' in item.properties) || 'selling_plan_allocation' in item
    );
  }

  async handleSubscribe() {
    try {
      this.showButtonLoader(this.subscribeButton);

      const activeTab = this.tabButtons.find(button => button.classList.contains('active'));

      if (!activeTab) {
        throw new Error('No active tab found');
      }

      const variantId = activeTab.dataset.variantId;
      const sellingPlan = activeTab.dataset.sellingPlan;

      if (!variantId || !sellingPlan) {
        throw new Error(`Missing data - VariantId: ${variantId}, SellingPlan: ${sellingPlan}`);
      }

      const validationPreviousProduct = await this.previousCartItem(variantId);

      if (validationPreviousProduct) {
        throw new Error('You already have this product in your cart');
      }

      const previousSubscriptionProduct = await this.getProductSubscriptionFromCart();

      if (previousSubscriptionProduct) {
        await this.deleteProductFromCart(previousSubscriptionProduct.key);
      }

      let entitlementPolicyId = null;
      let entitlementPolicyName = null;

      const customerScript = document.querySelector('.check-customer');
      let isUserLoggedIn = false;

      if (customerScript) {
        try {
          const customerData = JSON.parse(customerScript.textContent);
          isUserLoggedIn = customerData.logged_in;
        } catch (error) {
          console.error('Error parsing customer data:', error);
        }
      }

      try {
        const entitlementResponse = await fetch(`${this.apiBaseUrl}/entitlement-policies?variantId=${variantId}`);

        if (entitlementResponse.ok) {
          const entitlementsData = await entitlementResponse.json();

          let variantEntitlements = null;

          if (entitlementsData.length > 0) {
            variantEntitlements = entitlementsData.find(item => item.variantId.toString() === variantId);
          }

          if (variantEntitlements.variantEntitlementPolicies.length > 0) {
            entitlementPolicyId = variantEntitlements.variantEntitlementPolicies[0].entitlementPolicyV2.id;
            entitlementPolicyName = variantEntitlements.variantEntitlementPolicies[0].entitlementPolicyV2.title;
          }
        } else if (isUserLoggedIn) {
          throw new Error(`Error getting entitlement policies: ${entitlementResponse.status}`);
        }
      } catch (error) {
        if (isUserLoggedIn) {
          throw error;
        }

        console.warn('Could not get entitlement policies, but continuing as user is not logged in:', error);
      }

      if (!isUserLoggedIn && (!entitlementPolicyId || !entitlementPolicyName)) {
        entitlementPolicyId = 'pending_authentication';
        entitlementPolicyName = 'Pending Authentication';
      } else if (isUserLoggedIn && (!entitlementPolicyId || !entitlementPolicyName)) {
        throw new Error('No entitlement policy found');
      }

      const formData = {
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            selling_plan: parseInt(sellingPlan, 10),
            properties: {
              _subscription_variant: variantId,
              _requiresAuthentication: !isUserLoggedIn ? 'true' : 'false',
            },
          },
        ],
      };

      if (isUserLoggedIn) {
        formData.items[0].properties._entitlementPolicyId = entitlementPolicyId;
        formData.items[0].properties._entitlementPolicyName = entitlementPolicyName;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.description);
      }

      const responseData = await response.json();
      const productTitle = responseData.items && responseData.items[0] ? responseData.items[0].title : null;
      const variantTitle = responseData.items && responseData.items[0] ? responseData.items[0].variant_title : null;

      if (responseData.status) {
        if (this.cartForm) {
          this.cartForm.notify = responseData.description || responseData.message;
          this.cartForm.cartRefresh({ isSuccess: false, sections: [] });
        }

        throw new Error(responseData.description || responseData.message);
      }

      document.body.classList.remove('show-popup');
      this.popup.classList.remove('active');

      if (this.cartForm) {
        const sectionsToRender = this.cartForm.sections.map(section => section.section).join(',');

        const response = await fetch(
          `${window.location.pathname}?sections=${sectionsToRender}&last_added_variant=${variantId}`
        );

        const sections = await response.json();
        const originalSuccessNotify = this.cartForm.successNotify;
        this.cartForm.successNotify = function () {};

        this.cartForm.currentState = 'add';
        this.cartForm.cartRefresh({ isSuccess: true, sections });

        setTimeout(() => {
          let targetPopup = document.querySelector('.popup-noti-added');

          if (targetPopup) {
            const productTitleElement = targetPopup.querySelector('.product-title');

            if (productTitleElement) {
              const productName = productTitle;
              productTitleElement.textContent = productName;

              const optionTitle = targetPopup.querySelector('.product-option-title .text');

              if (optionTitle && variantTitle) {
                optionTitle.textContent = variantTitle;
              }
            }

            const productMedia = targetPopup.querySelector('.product-media');

            if (productMedia) {
              const hasImage = productMedia.querySelector(
                'img[src]:not([src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="])'
              );

              if (!hasImage) {
                productMedia.innerHTML = `
                  <div class="empty-image"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path><path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path></svg>
                  </div>
                `;
              }
            }

            targetPopup.style.display = 'block';

            setTimeout(() => {
              targetPopup.style.display = 'none';
            }, 5000);
          }

          this.cartForm.successNotify = originalSuccessNotify;
        }, 100);
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
        })
      );
    } catch (error) {
      console.error('Error:', error);
      this.bannerAlert(error.message);
    } finally {
      this.hideButtonLoader(this.subscribeButton);
    }
  }

  showButtonLoader(button) {
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent.trim();
    }

    if (!button.querySelector('.button-loader')) {
      const loader = document.createElement('span');
      loader.className = 'button-loader';
      loader.innerHTML = '<span class="spinner"></span>';

      loader.style.cssText = `
        display: inline-block;
        margin-left: 10px;
        vertical-align: middle;
      `;

      const style = document.createElement('style');

      if (!document.querySelector('#button-loader-style')) {
        style.id = 'button-loader-style';

        style.textContent = `
          .button-loader .spinner {
            display: inline-block;
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 50%;
            border-top-color: #fff;
            animation: spin 1s ease-in-out infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `;

        document.head.appendChild(style);
      }

      button.appendChild(loader);
    }

    button.disabled = true;
    button.classList.add('loading');
  }

  hideButtonLoader(button) {
    const loader = button.querySelector('.button-loader');

    if (loader) {
      loader.remove();
    }

    button.disabled = false;
    button.classList.remove('loading');
  }

  bannerAlert(message, title = 'Error') {
    const existingInfoBox = this.querySelector('.popup-main-content');

    const afterBanner = this.querySelector('.info-box');

    if (afterBanner) {
      afterBanner.remove();
    }

    const infoBox = document.createElement('div');

    infoBox.className = 'info-box red';

    infoBox.style.cssText = `
      padding: 16px 16px;
      display: grid;
      grid-template-columns: 24px 1fr 24px;
      align-items: start;
      border-radius: 4px;
      background: #fcf6e4;
      margin-bottom: 20px;
    `;

    infoBox.innerHTML = `
      <div class='icon-alert' style='margin-left: 8px;'>
        <div class='icon-info'>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path fill-rule='evenodd' clip-rule='evenodd' d='M23.0466 21L11.996 2L0.945312 21H23.0466ZM10.9914 18V16H13.0006V18H10.9914ZM10.9914 14H13.0006V10H10.9914V14Z' fill='#FAA422'/>
          </svg>
        </div>
      </div>
      <div class='message-alert' style='margin-left: 16px;'>
        <p style='color: #000; font-family: brandon-grotesque; font-size: 16px; font-weight: 450; line-height: 24px; margin: 0;'>${title}</p>
        <p style='color: #000; font-family: brandon-grotesque; font-size: 16px; font-weight: 390; line-height: 24px; margin: 0;'>${message}</p>
      </div>
    `;

    setTimeout(() => {
      infoBox.remove();
    }, 2000);

    existingInfoBox.insertAdjacentElement('afterbegin', infoBox);
  }
}

customElements.define('audio-subscription-component', AudioSubscriptionComponent);

window.openAudioSubscription = function () {
  const popup = document.querySelector('#audio-subscription');

  if (popup) {
    popup.setAttribute('aria-hidden', 'false');

    const requestCopy = document.querySelector('#request-copy');

    if (requestCopy) {
      requestCopy.setAttribute('data-current-step', '1');
    }
  }
};

document.addEventListener('DOMContentLoaded', function () {
  const audioPopup = document.querySelector('#audio-subscription');

  if (audioPopup) {
    setTimeout(() => {
      const hasShownPopup = sessionStorage.getItem('audioSubscriptionShown');

      if (!hasShownPopup) {
        window.openAudioSubscription();
        sessionStorage.setItem('audioSubscriptionShown', 'true');
      }
    }, 3000);
  }
});
